import { makeAutoObservable } from 'mobx'
import { CountryEnum, PhoneInputStore } from 'shared/ui'
import { isEmail } from 'shared/lib'
import { getEmailDomain, getUrlDomain } from 'shared/lib/getDomains'
import type { PointsOfContactItem } from 'entities/Compliance/model'
import { type InternationalStepsStore } from 'pages/settings/pages/compliance/pages/international/store/internationalStepsStore'

export class ContactInfoStore {
  private _firstName = ''
  private _lastName = ''
  private _email = ''
  private _emailLocalError = ''
  private _similarityDomainsWarning = ''
  phoneInputStore = new PhoneInputStore({ excludeCountries: [CountryEnum.AU] })

  constructor(private _internationalStepsStore: InternationalStepsStore) {
    makeAutoObservable(this)
  }

  get internationalStepsStore() {
    return this._internationalStepsStore
  }

  get contact() {
    return {
      firstName: this._firstName,
      lastName: this._lastName,
      email: this._email,
      phoneNumber: this.phoneInputStore.number,
    }
  }

  get isContactFilled() {
    if (!this.phoneInputStore.isValidNumber) return false

    return [this._firstName, this._lastName, this._email, this.phoneInputStore.number].every(
      (field) => field.trim().length > 0
    )
  }

  get fieldsError() {
    return this.internationalStepsStore.fieldsError
  }

  get isNextDisabled() {
    return (
      !this.isContactFilled ||
      !!this.fieldsError?.['email'] ||
      !!this.fieldsError?.['phone_number'] ||
      !!this.emailLocalError
    )
  }

  get similarityDomainsWarning() {
    return this._similarityDomainsWarning
  }

  get emailLocalError() {
    return this._emailLocalError
  }

  setSimilarityDomainsWarning = (value: string) => {
    this._similarityDomainsWarning = value
  }

  checkDomainSimilarity = (email: string) => {
    const { websiteUrl } = this._internationalStepsStore.businessInfoStore || {}

    if (!websiteUrl) return

    const emailDomain = getEmailDomain(email)
    if (!emailDomain) return false

    const urlObj = new URL(websiteUrl)
    const urlDomain = getUrlDomain(urlObj)
    if (!urlDomain) return false

    const isSimilar = emailDomain === urlDomain
    const errorText =
      "It looks like your business email domain doesn't match your business website domain"

    if (!isSimilar) {
      this.setSimilarityDomainsWarning(errorText)
    }
  }

  checkIsEmail = () => {
    const isCorrect = isEmail(this._email)

    if (isCorrect) {
      this.checkDomainSimilarity(this._email)
      this.setEmailLocalError('')
      return
    }

    const errorText = 'Please enter a valid email address'
    this.setEmailLocalError(errorText)
    this.setSimilarityDomainsWarning('')
  }

  setEmailLocalError = (error: string) => {
    this._emailLocalError = error
  }

  setFirstName = (name: string) => {
    this._firstName = name
  }

  setLastName = (name: string) => {
    this._lastName = name
  }

  setEmail = (email: string) => {
    this.setEmailLocalError('')
    this.setSimilarityDomainsWarning('')
    this._internationalStepsStore?.deleteErrorValue('email')

    this._email = email
  }

  setContactInfo = (contact: PointsOfContactItem) => {
    this._firstName = contact.first_name
    this._lastName = contact.last_name
    this._email = contact.email
    this.phoneInputStore.setNumber(contact.phone_number)
  }
}
